// © 2025 Camp Promethean, LLC. All Rights Reserved.
//
// Recorder — Hands-Free MVP voice capture, upload, and transcription UI.
//
// Orchestrates the full pipeline:
//   microphone → MediaRecorder → audio blob
//   → uploadAudioBlob (Supabase Storage voice-recordings bucket)
//   → callTranscribe (hands-free-transcribe edge function)
//   → onPipelineResult callback with the pipeline response
//
// Partial-success states (parse_failed, resolve_failed) are surfaced in the
// UI rather than treated as errors, so the user understands what succeeded.
//
// Auth / facility:
//   - Session token from supabase.auth.getSession() (same pattern as ReviewCard)
//   - facility_id and user_id from useFacility() and useAuth() contexts
//   - Storage path: {facility_id}/{user_id}/{timestamp}-{uuid}.{ext}
//
// Per ADR-002: audio bytes go directly to Supabase Storage from the browser.
// The edge function receives the storage_path, never the audio bytes.

import { useCallback, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Mic,
  MicOff,
  Square,
  RotateCcw,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useFacility } from "@/contexts/FacilityContext";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { hfCard, hfCardHeader, hfCardTitle, hfEmptyState, hfRecordButton, hfRecordPulse, hfSuccessBanner, hfWarnAlert, hfWarnBody, hfWarnTitle } from "./handsFreeUi";
import type { HandsFreeContext, PipelineResult, WorkflowType } from "./types";
import { WORKFLOW_LABELS, WORKFLOW_TYPES } from "./types";
import {
  buildStoragePath,
  callTranscribe,
  uploadAudioBlob,
} from "./transcribe";
import { useRecorder } from "./useRecorder";

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatElapsed(seconds: number): string {
  const m = Math.floor(seconds / 60)
    .toString()
    .padStart(2, "0");
  const s = (seconds % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

// ── Pipeline stage for UI display ────────────────────────────────────────────

type PipelineStage =
  | "idle"
  | "uploading"
  | "transcribing"
  | "done";

// ── Props ─────────────────────────────────────────────────────────────────────

interface RecorderProps {
  /**
   * Called when the pipeline produces a result (success or partial).
   * The parent page decides what to do with the review_item_id.
   */
  onPipelineResult: (result: PipelineResult) => void;
  /** Called when the recorder is reset (clears any existing review card). */
  onReset?: () => void;
  /**
   * Pin the workflow (e.g. when embedded in a runoff logger page). When set,
   * the workflow selector is hidden and this workflow is always used.
   */
  fixedWorkflow?: WorkflowType;
  /** Page-context pins (crop_id, etc.) forwarded to the edge function. */
  context?: HandsFreeContext;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function Recorder({ onPipelineResult, onReset, fixedWorkflow, context }: RecorderProps) {
  const { profile } = useAuth();
  const { activeFacility } = useFacility();

  const {
    micPermission,
    recordingState,
    elapsedSeconds,
    audioBlob,
    mimeType,
    extension,
    requestPermission,
    start,
    stop,
    reset: resetRecorder,
  } = useRecorder();

  const [pipelineStage, setPipelineStage] = useState<PipelineStage>("idle");
  const [pipelineError, setPipelineError] = useState<string | null>(null);
  const [partialResult, setPartialResult] = useState<PipelineResult | null>(
    null,
  );
  // Which log type this recording is for. "auto" lets the intent router (T-207)
  // pick from the words; an explicit choice always wins. A fixedWorkflow prop
  // (embedded mode) pins this and hides the selector.
  const [workflow, setWorkflow] = useState<WorkflowType | "auto">(
    fixedWorkflow ?? "auto",
  );

  // ── Upload + transcribe ──────────────────────────────────────────────────────

  const runPipeline = useCallback(
    async (blob: Blob, mime: string, ext: string) => {
      if (!profile?.id || !activeFacility?.id) return;

      setPipelineError(null);
      setPartialResult(null);

      // Step 1: upload
      setPipelineStage("uploading");

      const storagePath = buildStoragePath({
        facilityId: activeFacility.id,
        userId: profile.id,
        extension: ext,
      });

      const uploadResult = await uploadAudioBlob(blob, storagePath, mime);
      if (!uploadResult.ok) {
        setPipelineError(uploadResult.error);
        setPipelineStage("idle");
        return;
      }

      // Step 2: call hands-free-transcribe
      setPipelineStage("transcribing");

      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) {
        setPipelineError(
          "Session expired. Please refresh the page and try again.",
        );
        setPipelineStage("idle");
        return;
      }

      const supabaseUrl =
        (import.meta as Record<string, unknown>).env
          ?.VITE_SUPABASE_URL as string | undefined ||
        "https://bjvlynqjoetvrbbfvhod.supabase.co";

      const transcribeResult = await callTranscribe({
        storagePath: uploadResult.storagePath,
        facilityId: activeFacility.id,
        accessToken,
        supabaseUrl,
        workflowHint: workflow,
        context: context
          ? { ...context, ...(fixedWorkflow ? { workflow: fixedWorkflow } : {}) }
          : fixedWorkflow
            ? { workflow: fixedWorkflow }
            : undefined,
      });

      setPipelineStage("done");

      if (!transcribeResult.ok) {
        setPipelineError(transcribeResult.error);
        return;
      }

      // The chosen workflow drives the pipeline; stamp it on the result so the
      // ReviewCard renders the right fields without an extra fetch.
      const result: PipelineResult = {
        ...transcribeResult.result,
        workflow_type: transcribeResult.result.workflow_type ?? workflow,
      };

      // Partial successes (200 with non-terminal status) surface as partial results
      if (
        result.status === "parse_failed" ||
        result.status === "resolve_failed"
      ) {
        setPartialResult(result);
        return;
      }

      // Full success (pending_review or review_required) — hand off to parent
      onPipelineResult(result);
    },
    [profile?.id, activeFacility?.id, onPipelineResult, workflow],
  );

  // Trigger pipeline as soon as stop produces a blob
  const handleStop = useCallback(() => {
    stop();
    // blob is set asynchronously by MediaRecorder.onstop; we watch it in the
    // effect below via the recordingState transition to 'stopped'
  }, [stop]);

  // When recordingState transitions to 'stopped', audioBlob is ready
  // We use a separate effect via a local ref to avoid stale closure issues;
  // instead we expose audioBlob directly and let Recorder drive the pipeline.
  const handleRunFromBlob = useCallback(() => {
    if (audioBlob && mimeType && extension) {
      runPipeline(audioBlob, mimeType, extension);
    }
  }, [audioBlob, mimeType, extension, runPipeline]);

  // ── Reset everything ─────────────────────────────────────────────────────────

  const handleReset = useCallback(() => {
    resetRecorder();
    setPipelineStage("idle");
    setPipelineError(null);
    setPartialResult(null);
    onReset?.();
  }, [resetRecorder, onReset]);

  // ── Guard: no facility selected ──────────────────────────────────────────────

  if (!activeFacility) {
    return (
      <Card>
        <CardContent className="pt-6">
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>No facility selected</AlertTitle>
            <AlertDescription>
              Select an active facility from your profile before using the voice
              recorder. The recording is scoped to your current facility.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  // ── Guard: browser does not support MediaRecorder ────────────────────────────

  if (micPermission === "unavailable") {
    return (
      <Card>
        <CardContent className="pt-6">
          <Alert variant="destructive">
            <MicOff className="h-4 w-4" />
            <AlertTitle>Voice recording not supported</AlertTitle>
            <AlertDescription>
              Your browser does not support the MediaRecorder API. Try Chrome,
              Firefox, or Safari 14.1+ on a device with a microphone.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  const isProcessing =
    pipelineStage === "uploading" || pipelineStage === "transcribing";
  const showRunButton =
    recordingState === "stopped" &&
    audioBlob !== null &&
    pipelineStage === "idle" &&
    !pipelineError &&
    !partialResult;

  return (
    <Card className={hfCard}>
      <CardHeader className={hfCardHeader}>
        <CardTitle className={cn(hfCardTitle, "flex items-center gap-2")}>
          <Mic className="h-4 w-4 text-primary" />
          Voice
        </CardTitle>
        <CardDescription>
          Say it naturally — room, zone, and readings. We'll draft the entry for you to review.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* ── Workflow selector (hidden when pinned via fixedWorkflow) ─────── */}
        {!fixedWorkflow && (
          <div className="flex flex-wrap gap-1.5">
            {(["auto", ...WORKFLOW_TYPES] as const).map((w) => (
              <Button
                key={w}
                type="button"
                size="sm"
                variant={workflow === w ? "default" : "outline"}
                onClick={() => setWorkflow(w)}
                disabled={isProcessing || recordingState === "recording"}
                className="text-xs"
              >
                {w === "auto" ? "Auto-detect" : WORKFLOW_LABELS[w]}
              </Button>
            ))}
          </div>
        )}

        {/* ── Mic permission flow ─────────────────────────────────────────── */}
        {micPermission === "idle" && (
          <div className={cn(hfEmptyState, "py-10")}>
            <p className="mb-4 max-w-sm text-sm text-muted-foreground">
              Allow microphone access, then tap the button and speak your log entry.
            </p>
            <Button onClick={requestPermission}>
              <Mic className="mr-2 h-4 w-4" />
              Enable microphone
            </Button>
          </div>
        )}

        {micPermission === "requesting" && (
          <div className="flex items-center justify-center gap-2 py-4 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Requesting microphone access…
          </div>
        )}

        {micPermission === "denied" && (
          <Alert variant="destructive">
            <MicOff className="h-4 w-4" />
            <AlertTitle>Microphone access denied</AlertTitle>
            <AlertDescription>
              Allow microphone access in your browser's site settings, then
              reload the page.
            </AlertDescription>
          </Alert>
        )}

        {/* ── Recording controls ───────────────────────────────────────────── */}
        {(micPermission === "granted") &&
          recordingState !== "stopped" &&
          !isProcessing && (
            <div className="flex flex-col items-center gap-5 py-6">
              {recordingState === "idle" && (
                <div className="relative flex items-center justify-center">
                  <span className={hfRecordPulse} aria-hidden />
                  <Button
                    onClick={start}
                    size="lg"
                    className={hfRecordButton}
                    aria-label="Start recording"
                  >
                    <Mic className="h-8 w-8" />
                  </Button>
                </div>
              )}

              {recordingState === "recording" && (
                <>
                  <div className="relative flex items-center justify-center">
                    <span className="absolute inline-flex h-20 w-20 animate-ping rounded-full bg-destructive/30" />
                    <Button
                      onClick={handleStop}
                      size="lg"
                      variant="destructive"
                      className="relative h-20 w-20 rounded-full shadow-lg"
                      aria-label="Stop recording"
                    >
                      <Square className="h-7 w-7 fill-current" />
                    </Button>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-destructive" />
                    <span className="font-mono text-lg tabular-nums">{formatElapsed(elapsedSeconds)}</span>
                  </div>
                  <p className="text-sm text-muted-foreground">Listening…</p>
                </>
              )}
            </div>
          )}

        {/* ── Stopped — confirm before pipeline ───────────────────────────── */}
        {recordingState === "stopped" && showRunButton && (
          <div className="flex flex-col items-center gap-4 py-4">
            <p className="text-sm text-muted-foreground">
              Got it ({formatElapsed(elapsedSeconds)}). Ready to process?
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleReset}>
                <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                Re-record
              </Button>
              <Button onClick={handleRunFromBlob}>
                <Mic className="mr-1.5 h-4 w-4" />
                Process recording
              </Button>
            </div>
          </div>
        )}

        {isProcessing && (
          <div className="flex flex-col items-center gap-3 py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm font-medium">
              {pipelineStage === "uploading"
                ? "Sending your recording…"
                : "Understanding what you said…"}
            </p>
            <p className="text-xs text-muted-foreground">This usually takes a few seconds</p>
          </div>
        )}

        {pipelineError && (
          <div className="space-y-3">
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Something went wrong</AlertTitle>
              <AlertDescription>{pipelineError}</AlertDescription>
            </Alert>
            <Button variant="outline" size="sm" onClick={handleReset}>
              <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
              Try again
            </Button>
          </div>
        )}

        {partialResult?.status === "parse_failed" && (
          <div className="space-y-3">
            <Alert className={hfWarnAlert}>
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle className={hfWarnTitle}>Couldn't pull out the details</AlertTitle>
              <AlertDescription className={hfWarnBody}>
                <p className="mb-2">
                  We heard you, but couldn't turn it into a structured log. Try speaking more clearly
                  and include location and your measurements.
                </p>
                {partialResult.raw_transcript && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-xs font-medium">What we heard</summary>
                    <blockquote className="mt-2 rounded-lg bg-background/60 px-3 py-2 text-sm leading-relaxed text-foreground">
                      {partialResult.raw_transcript}
                    </blockquote>
                  </details>
                )}
              </AlertDescription>
            </Alert>
            <Button variant="outline" size="sm" onClick={handleReset}>
              <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
              Try again
            </Button>
          </div>
        )}

        {partialResult?.status === "resolve_failed" && (
          <div className="space-y-3">
            <Alert className={hfWarnAlert}>
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle className={hfWarnTitle}>Couldn't match your location</AlertTitle>
              <AlertDescription className={hfWarnBody}>
                <p className="mb-2">
                  We got your readings but couldn't match the location. Try using the exact room
                  and zone names from your facility, or pick the location when you review.
                </p>
                {partialResult.raw_transcript && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-xs font-medium">What we heard</summary>
                    <blockquote className="mt-2 rounded-lg bg-background/60 px-3 py-2 text-sm leading-relaxed text-foreground">
                      {partialResult.raw_transcript}
                    </blockquote>
                  </details>
                )}
              </AlertDescription>
            </Alert>
            <Button variant="outline" size="sm" onClick={handleReset}>
              <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
              Try again
            </Button>
          </div>
        )}

        {pipelineStage === "done" &&
          !pipelineError &&
          !partialResult && (
            <div className={cn("flex items-center gap-2 rounded-lg border px-4 py-3 text-sm", hfSuccessBanner)}>
              <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" />
              <span>Ready for your review below.</span>
            </div>
          )}
      </CardContent>
    </Card>
  );
}

export default Recorder;
